import { useState } from "react";
import type { OutbreakCase } from "@/data/cases";
import type { Lang } from "@/lib/i18n";

interface Props {
  country: string;
  cases: OutbreakCase[];
  language: Lang;
}

export default function AISummary({ country, cases, language }: Props) {
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function fetchSummary() {
    setLoading(true);
    setError(null);
    setSummary(null);
    try {
      const res = await fetch("/api/ai-summary", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          country,
          language,
          cases: cases.map((c) => ({
            status: c.status,
            city: c.city,
            notes: c.notes,
            onsetDate: c.onsetDate,
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to generate summary");
      } else {
        setSummary(data.summary);
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="border-t border-border pt-2">
      {!summary && !loading && (
        <button
          onClick={fetchSummary}
          className="w-full text-[10px] uppercase tracking-wider font-bold py-1.5 border border-hvi-primary/40 text-hvi-primary hover:bg-hvi-primary/10 transition"
        >
          ✦ AI Analysis
        </button>
      )}
      {loading && (
        <div className="text-[10px] text-muted-foreground py-1.5 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-hvi-primary animate-pulse" />
          Analyzing…
        </div>
      )}
      {summary && (
        <div className="space-y-1.5">
          <div className="text-[9px] uppercase tracking-wider text-hvi-primary font-bold">✦ AI Summary</div>
          <p className="text-[11px] leading-relaxed text-hvi-on-surface">{summary}</p>
          <span className="text-[9px] text-muted-foreground block">Powered by Lovable AI · Gemini Flash</span>
        </div>
      )}
      {error && (
        <p className="text-[10px] text-red-400 mt-1">{error}</p>
      )}
    </div>
  );
}
